import React, { useEffect, useRef, useState } from 'react';
import Seo from '@/components/seo';
import Turnstile from 'react-turnstile';
import { Button, Card, Col, Container, Form, Row, } from 'react-bootstrap';
import { FaEnvelope, FaWhatsapp, FaClock, FaPaperPlane, FaHeadset } from 'react-icons/fa6';
import { InputMask } from 'primereact/inputmask';
import { http, layout, messages, ref, validate, } from '@/helpers/index';
import { auth } from '@/services/auth';
import { constant } from '@/helpers/constant';

function Contact() {
  const user = auth.store.logged.get();
  const documentRef = useRef();
  const initial = {
    type: 'GENERAL',
    product: '',
    plate: '',
    name: user ? user.name : '',
    email: user ? user.email : '',
    mobile: user ? user.mobile : '',
    message: '',
    turnstile: '',
  };
  const [body, setBody] = useState(initial);
  const [products, setProducts] = useState();

  useEffect(() => {
    http.get('/products/home', (result, resultError) => {
      if (resultError) {
        setProducts();
        return;
      }
      if (result) {
        setProducts(result);
      }
    }, auth.headers());
  }, []);

  const handleInputChange = ({ target }) => {
    setBody({ ...body, [target.name]: target.value });
  };

  async function onSubmit(e) {
    e.preventDefault();
    messages.clear();
    if (!body.email || !validate.email(body.email)) {
      messages.error('Por favor, informe um E-mail válido.', false, true);
      e.currentTarget.email.focus();
      return;
    }
    if (!body.mobile || body.mobile.replaceAll(/[()-\s]/g, '').length < 11) {
      messages.error('Por favor, informe um Telefone Celular válido.', false, true);
      e.currentTarget.mobile.focus();
      return;
    }
    layout.loading(true);
    const formData = new FormData();
    if (ref.files.check(documentRef)) {
      formData.append('document', documentRef.current.files[0]);
    }
    formData.append('type', body.type);
    if (body.product) {
      formData.append('product', body.product);
    }
    if (body.plate) {
      formData.append('plate', body.plate.replaceAll(/[_-]/g, '').toUpperCase());
    }
    formData.append('name', body.name);
    formData.append('email', body.email);
    formData.append('mobile', body.mobile);
    formData.append('message', body.message);
    formData.append('turnstile', body.turnstile);
    http.post('/contact', formData, (result) => {
      setBody({ ...body, turnstile: '' });
      layout.loading(false);
      if (result) {
        setBody(initial);
        messages.success('Agradecemos seu contato! Em breve te retornaremos.', false, true);
        return;
      }
      messages.error('Erro ao enviar mensagem! Por favor, tente novamente dentro de alguns minutos.', false, true);
    }, true);
  }

  const contactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contato e Suporte - AboutCar',
    description: 'Entre em contato com a equipe de suporte da AboutCar. Tire suas dúvidas sobre laudos e consultas de placa.',
    url: 'https://aboutcar.com.br/contato'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Contato',
        item: 'https://aboutcar.com.br/contato'
      }
    ]
  };

  return (
    <>
      <Seo
        title="Fale Conosco - Contato e Suporte | AboutCar"
        description="Entre em contato com a equipe de suporte da AboutCar. Tire suas dúvidas, envie sugestões ou peça auxílio sobre seus laudos e consultas de placa."
        keywords="contato aboutcar, suporte consulta veicular, atendimento consulta placa, ajuda laudo veicular"
        schema={[contactPageSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-light-subtle border-bottom py-5">
        <Container className="py-2">
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <span className="badge bg-primary text-white rounded-pill px-3 py-1 text-uppercase fw-bold mb-3 fs-7">AboutCar + Você</span>
              <h1 className="display-4 fw-extrabold text-dark mb-3">
                Aqui, com certeza você tem <span className="text-primary">prioridade!</span>
              </h1>
              <p className="lead text-secondary mb-4">
                Experimente nossa excelência ao lhe atender. Nossa equipe está pronta para responder suas dúvidas e auxiliar em seus laudos e consultas.
              </p>

              {/* Canal de Atendimento em Destaque */}
              <Row className="g-3">
                <Col sm={6}>
                  <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle h-100 d-flex align-items-center gap-3">
                    <div className="bg-primary-subtle text-primary p-2 rounded-3">
                      <FaEnvelope className="fs-4" />
                    </div>
                    <div>
                      <span className="d-block text-muted small fw-semibold">E-mail</span>
                      <strong className="text-dark small text-break">contato@aboutcar.com.br</strong>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle h-100 d-flex align-items-center gap-3">
                    <div className="bg-success-subtle text-success p-2 rounded-3">
                      <FaHeadset className="fs-4" />
                    </div>
                    <div>
                      <span className="d-block text-muted small fw-semibold">Suporte Agil</span>
                      <strong className="text-dark small">Seg a Sex (09h às 18h)</strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col lg={5} className="text-center">
              <img src="/assets/images/contact-us.svg" className="img-fluid max-h-350" border="0" alt="Fale Conosco" loading="lazy" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Form Section */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            <Card className="border-0 shadow-sm rounded-4 p-4 p-md-5">
              <div className="text-center mb-4">
                <h2 className="display-6 fw-bold text-dark mb-2">Deseja entrar em contato conosco?</h2>
                <p className="text-secondary small">Preencha o formulário abaixo e em breve retornaremos seu contato.</p>
              </div>

              <Form onSubmit={onSubmit}>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="type" className="fw-semibold text-secondary small mb-1">Tipo do Contato</Form.Label>
                      <Form.Select
                        id="type"
                        name="type"
                        value={body.type}
                        required
                        className="form-select-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        onChange={handleInputChange}
                        autoFocus
                      >
                        <option value="GENERAL">Geral</option>
                        <option value="SUPPORT">Suporte Técnico / Dúvidas</option>
                        <option value="FINANCE">Financeiro / Pagamentos</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {body.type === 'SUPPORT' && (
                    <>
                      <Col xs={12} sm={8}>
                        <Form.Group>
                          <Form.Label htmlFor="product" className="fw-semibold text-secondary small mb-1">Produto</Form.Label>
                          <Form.Select
                            name="product"
                            id="product"
                            required
                            className="form-select-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                            value={body.product}
                            onChange={handleInputChange}
                          >
                            <option value="">Selecione o Produto</option>
                            {products?.map((el) => (
                              <option value={`${el.code} - ${el.name}`} key={el.code}>{el.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} sm={4}>
                        <Form.Group>
                          <Form.Label htmlFor="plate" className="fw-semibold text-secondary small mb-1">Placa do Veículo</Form.Label>
                          <InputMask
                            id="plate"
                            name="plate"
                            className="form-control form-control-lg rounded-3 py-2 px-3 input-uppercase shadow-sm border-light-subtle"
                            mask="aaa9*99"
                            slotChar=""
                            required
                            value={body.plate}
                            onChange={(e) => setBody({ ...body, plate: e.value })}
                            placeholder="ABC1D23"
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="name" className="fw-semibold text-secondary small mb-1">Seu Nome Completo</Form.Label>
                      <Form.Control
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Digite seu nome completo"
                        required
                        className="form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        value={body.name}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={7}>
                    <Form.Group>
                      <Form.Label htmlFor="email" className="fw-semibold text-secondary small mb-1">E-mail para Resposta</Form.Label>
                      <Form.Control
                        type="email"
                        id="email"
                        name="email"
                        placeholder="seuemail@exemplo.com.br"
                        required
                        className="form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        value={body.email}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={5}>
                    <Form.Group>
                      <Form.Label htmlFor="mobile" className="fw-semibold text-secondary small mb-1">Telefone WhatsApp</Form.Label>
                      <InputMask
                        id="mobile"
                        name="mobile"
                        className="form-control form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        mask="(99) 99999-9999"
                        slotChar=""
                        required
                        value={body.mobile}
                        onChange={(e) => setBody({ ...body, mobile: e.value })}
                        placeholder="(00) 00000-0000"
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="message" className="fw-semibold text-secondary small mb-1">Mensagem</Form.Label>
                      <Form.Control
                        as="textarea"
                        id="message"
                        name="message"
                        placeholder="Escreva sua mensagem com detalhes..."
                        required
                        className="form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        value={body.message}
                        onChange={handleInputChange}
                        rows={5}
                      />
                    </Form.Group>
                  </Col>

                  {body.type === 'FINANCE' && (
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label htmlFor="document" className="fw-semibold text-secondary small mb-1">Deseja enviar um comprovante?</Form.Label>
                        <Form.Control
                          id="document"
                          name="document"
                          type="file"
                          required
                          className="rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                          onChange={handleInputChange}
                          accept="image/png, image/jpeg, application/pdf"
                          ref={documentRef}
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <div className="text-center mt-4 pt-2">
                  <Button type="submit" size="lg" className="btn-gradient-primary rounded-pill px-5 py-3 fw-bold border-0 shadow fs-5 w-100 w-md-auto" disabled={!body.turnstile}>
                    {body.turnstile ? (
                      <>
                        <FaPaperPlane className="me-2" />
                        Enviar Mensagem
                      </>
                    ) : (
                      <span>Validando acesso...</span>
                    )}
                  </Button>
                </div>
              </Form>

              <Turnstile sitekey={constant.turnstile.siteKey} onVerify={(token) => { setBody({ ...body, turnstile: token }); }} />
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

Contact.enableMessages = true;
export default Contact;
